import type { AlbumConfig, StickerInvestment } from '../types'
import { THEME_DEFINITIONS, formatCurrency, stickerTitle, stickerValue } from '../data'
import { useI18n } from '../i18n/context'

interface HistoryRow {
  stickerNumber: number
  name: string
  value: number
  investedAt: string
  destinationName: string
  comments: string
}

interface Props {
  album: AlbumConfig
  stickers: Record<number, StickerInvestment>
  headerActions?: JSX.Element
}

export function HistoryView({ album, stickers, headerActions }: Props): JSX.Element {
  const { t } = useI18n()
  const theme = THEME_DEFINITIONS[album.theme]

  const rows: HistoryRow[] = Object.values(stickers)
    .map((s) => {
      const dest = album.savedInvestments.find((d) => d.id === s.destinationId)
      return {
        stickerNumber: s.stickerNumber,
        name: stickerTitle(theme, s.stickerNumber),
        value: stickerValue(s.stickerNumber, album.size),
        investedAt: s.investedAt,
        destinationName: dest ? `${dest.name} (${dest.kind})` : `Unknown (${s.destinationId})`,
        comments: s.comments
      }
    })
    .sort((a, b) => new Date(b.investedAt).getTime() - new Date(a.investedAt).getTime())

  const totalInvested = rows.reduce((sum, r) => sum + r.value, 0)

  function exportCSV(): void {
    const header = [
      t.stickerCol,
      t.nameCol,
      `${t.valueCol} (${album.currency})`,
      t.dateCol,
      t.destinationCol,
      t.commentsCol
    ].join(',')
    const csvRows = rows.map((r) =>
      [
        r.stickerNumber,
        `"${r.name.replace(/"/g, '""')}"`,
        r.value,
        new Date(r.investedAt).toLocaleString(),
        `"${r.destinationName.replace(/"/g, '""')}"`,
        `"${r.comments.replace(/"/g, '""')}"`
      ].join(',')
    )
    const csv = [header, ...csvRows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${album.name.replace(/[^a-z0-9]/gi, '_')}_history.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section className="history-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">{t.investmentHistory}</p>
          <h2>{t.allRecordedInvestments}</h2>
        </div>
        <div className="panel-heading-actions">
          {headerActions}
          <button className="ghost-button" onClick={exportCSV} type="button">
            {t.exportCSV}
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="empty-state">
          <strong>{t.noInvestmentsYet}</strong>
          <p>{t.noInvestmentsDesc}</p>
        </div>
      ) : (
        <>
          <div className="history-summary">
            <span>{t.investmentsCount(rows.length)}</span>
            <strong>
              {t.total}: {formatCurrency(totalInvested, album.currency)}
            </strong>
          </div>

          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr>
                  <th>{t.stickerCol}</th>
                  <th>{t.nameCol}</th>
                  <th>{t.valueCol}</th>
                  <th>{t.dateCol}</th>
                  <th>{t.destinationCol}</th>
                  <th>{t.commentsCol}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.stickerNumber}>
                    <td className="col-number">{row.stickerNumber}</td>
                    <td>{row.name}</td>
                    <td className="col-value">{formatCurrency(row.value, album.currency)}</td>
                    <td className="col-date">{new Date(row.investedAt).toLocaleDateString()}</td>
                    <td>{row.destinationName}</td>
                    <td className="col-comments">{row.comments || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>
                    <strong>{t.total}</strong>
                  </td>
                  <td className="col-value">
                    <strong>{formatCurrency(totalInvested, album.currency)}</strong>
                  </td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
